import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
	closestCenter,
	pointerWithin,
	rectIntersection,
	DndContext,
	DragOverlay,
	getFirstCollision,
	MouseSensor,
	TouchSensor,
	useSensors,
	useSensor,
	MeasuringStrategy,
	defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@material-ui/core";
import DroppableContainer from "./DroppableContainer";
import { AddRounded } from "@material-ui/icons";

// For the drag overlay's drop animation
const dropAnimation = {
	sideEffects: defaultDropAnimationSideEffects({
		styles: {
			active: { opacity: "0.5" },
		},
	}),
};

export default function MultipleContainers({
	items,
	onItemsChange,
	adjustScale = false,
	cancelDrop,
	modifiers,
	renderItem,
	renderHeader,
	isPreviewMode,
	canAddContainer = true,
	canAddContainerItem,
	resources,
	containersWithErrors = {},
	containersErrorMessages = {},
	isAddingNewSegment = false,
	onAccordionToggle,
	forceExpandErrors = false,
	validationId = null,
}) {
	const [containers, setContainers] = useState(Object.keys(items));

	useEffect(() => {
		setContainers(Object.keys(items));
	}, [items]);

	const [activeId, setActiveId] = useState(null);

	const [expandedMap, setExpandedMap] = useState(() => {
		const init = {};
		for (const containerId of containers) {
			init[containerId] = true;
		}
		return init;
	});

	// Auto-expand containers with errors based on validation state
	useEffect(() => {
		// Skip if we're in the process of adding a new segment
		if (isAddingNewSegment) {
			return;
		}

		if (forceExpandErrors) {
			setExpandedMap((prev) => {
				const newExpandedMap = { ...prev };

				Object.keys(containersWithErrors).forEach((containerId) => {
					// Only expand if we're forcing expansion
					if (containersWithErrors[containerId]) {
						newExpandedMap[containerId] = true;
					}
				});

				return newExpandedMap;
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAddingNewSegment, forceExpandErrors, validationId]);

	function handleToggleExpand(containerId) {
		setExpandedMap((prev) => {
			const newState = !prev[containerId];

			// Notify parent about accordion toggle for tracking user interaction
			if (onAccordionToggle) {
				onAccordionToggle(containerId, newState);
			}

			return {
				...prev,
				[containerId]: newState,
			};
		});
	}

	function updateItems(newItems) {
		onItemsChange(newItems);
	}

	const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
	const lastOverId = useRef(null);
	const recentlyMovedToNewContainer = useRef(false);

	const nextItemIdRef = useRef(1);
	function getNextItemId() {
		const newId = `Item${nextItemIdRef.current++}`;
		return newId;
	}

	function handleAddItem(containerId) {
		const newItems = {
			...items,
			[containerId]: [...items[containerId], getNextItemId()],
		};
		updateItems(newItems);
	}

	function handleAddColumn() {
		const newContainerId = getNextContainerId();
		setContainers((prev) => [...prev, newContainerId]);

		setExpandedMap((prev) => ({
			...prev,
			[newContainerId]: true,
		}));

		// Create a first item automatically when adding a new segment
		const newItemId = getNextItemId();
		const newItems = {
			...items,
			[newContainerId]: [newItemId],
		};
		updateItems(newItems);
	}

	const handleDeleteContainer = (containerId) => {
		setContainers((prev) => prev.filter((id) => id !== containerId));
		const newItems = { ...items };
		delete newItems[containerId];
		updateItems(newItems);
	};

	const handleDeleteItem = (containerId, itemId) => {
		const newItems = {
			...items,
			[containerId]: items[containerId].filter((id) => id !== itemId),
		};
		updateItems(newItems);
	};

	function getNextContainerId() {
		const id = `container-new_${containerIdCounterRef.current++}`;
		return id;
	}

	const containerIdCounterRef = useRef(1);

	const collisionDetectionStrategy = useCallback(
		(args) => {
			if (activeId && activeId in items) {
				// dragging a container
				return closestCenter({
					...args,
					droppableContainers: args.droppableContainers.filter(
						(container) => container.id in items
					),
				});
			}
			const pointerIntersections = pointerWithin(args);
			const intersections =
				pointerIntersections.length > 0
					? pointerIntersections
					: rectIntersection(args);

			let overId = getFirstCollision(intersections, "id");
			if (overId != null) {
				if (overId in items) {
					const containerItems = items[overId];
					if (containerItems.length > 0) {
						overId = closestCenter({
							...args,
							droppableContainers: args.droppableContainers.filter(
								(c) => c.id !== overId && containerItems.includes(c.id)
							),
						})[0]?.id;
					}
				}
				lastOverId.current = overId;
				return [{ id: overId }];
			}
			if (recentlyMovedToNewContainer.current) {
				lastOverId.current = activeId;
			}
			return lastOverId.current ? [{ id: lastOverId.current }] : [];
		},
		[activeId, items]
	);

	useEffect(() => {
		requestAnimationFrame(() => {
			recentlyMovedToNewContainer.current = false;
		});
	}, [items]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={collisionDetectionStrategy}
			measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
			cancelDrop={cancelDrop}
			onDragStart={({ active }) => {
				setActiveId(active.id);
			}}
			onDragEnd={({ active, over }) => {
				if (!over?.id) {
					setActiveId(null);
					return;
				}

				// If dragging a container
				if (active.id in items) {
					const overId = over.id;
					if (!(overId in items)) {
						setActiveId(null);
						return;
					}

					setContainers((prevContainers) => {
						const activeIndex = prevContainers.indexOf(active.id);
						const overIndex = prevContainers.indexOf(overId);
						if (
							activeIndex !== -1 &&
							overIndex !== -1 &&
							activeIndex !== overIndex
						) {
							const newArr = arrayMove(prevContainers, activeIndex, overIndex);
							setContainers(newArr);
							const reordered = reorderObjectKeys(items, newArr);
							updateItems(reordered);
							return newArr;
						}
						return prevContainers;
					});

					setActiveId(null);
					return;
				}

				setActiveId(null);
			}}
			modifiers={modifiers}
		>
			<SortableContext
				items={containers}
				strategy={verticalListSortingStrategy}
			>
				{containers.map((containerId) => (
					<DroppableContainer
						key={containerId}
						id={containerId}
						items={items[containerId]}
						onRemove={() => handleDeleteContainer(containerId)}
						header={renderHeader(containerId)}
						renderItem={(itemId) => renderItem(itemId, containerId)}
						onAddItem={handleAddItem}
						onItemsReorder={(newItemsForThisContainer) => {
							const newItemsObject = {
								...items,
								[containerId]: newItemsForThisContainer,
							};
							updateItems(newItemsObject);
						}}
						onItemRemove={(itemId) => handleDeleteItem(containerId, itemId)}
						canAddItem={
							canAddContainerItem ? canAddContainerItem(containerId) : true
						}
						expanded={expandedMap[containerId]}
						onToggleExpand={() => handleToggleExpand(containerId)}
						isPreviewMode={isPreviewMode}
						resources={resources}
						hasValidationErrors={containersWithErrors[containerId] || false}
						validationErrorMessage={containersErrorMessages[containerId] || false} 
					/>
				))}

				{!isPreviewMode && (
					<Button
						id="addSegment"
						variant="contained"
						color="primary"
						onClick={handleAddColumn}
						startIcon={<AddRounded />}
						disabled={!canAddContainer}
					>
						{resources["AddChecklistSegment"]}
					</Button>
				)}
			</SortableContext>

			{createPortal(
				<DragOverlay
					style={{ zIndex: 9999 }}
					adjustScale={adjustScale}
					dropAnimation={dropAnimation}
				>
					{renderContainerDragOverlay(activeId)}
				</DragOverlay>,
				document.body
			)}
		</DndContext>
	);

	function renderContainerDragOverlay(containerId) {
		// Container overlay changes inputs ids to prevent duplicate ids warnings
		const isContainerOverlay = true;

		if (!containerId || !items[containerId]) {
			return null;
		}
		return (
			<DroppableContainer
				key={containerId}
				id={containerId}
				items={items[containerId]}
				header={renderHeader(containerId, isContainerOverlay)}
				renderItem={(itemId) =>
					renderItem(itemId, containerId, isContainerOverlay)
				}
				onRemove={() => handleDeleteContainer(containerId)}
				onItemRemove={(itemId) =>
					handleDeleteItem(containerId, itemId)
				}
				expanded={expandedMap[containerId]}
				onToggleExpand={() => handleToggleExpand(containerId)}
				shadow
				disabled
				resources={resources}
				isDragOverlay={true}
			/>
		);
	}

	function reorderObjectKeys(originalObj, orderedKeys) {
		const newObj = {};
		for (const key of orderedKeys) {
			newObj[key] = originalObj[key];
		}
		return newObj;
	}
}
